import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Spinner,
} from "@fluentui/react-components";
import { CheckmarkRegular, DismissRegular } from "@fluentui/react-icons";
import { useCallback, useEffect, useState } from "react";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { PeoplePicker } from "components/fields/peoplePicker/PeoplePicker";
import { CustomOption } from "components/fields/peoplePicker/PeoplePicker.types";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
} from "theme/sharedStyles";
import { toUserOption } from "utils/generalUtils";
import { usePortfoliosPracticesAccessStyles } from "./PortfoliosPracticesAccess.styles";
import { IExecutiveAccessDialogProps } from "./PortfoliosPracticesAccess.types";

export const ExecutiveAccessDialog = ({
  isOpen,
  isSaving,
  editingItem = null,
  onSave,
  onDismiss,
}: IExecutiveAccessDialogProps): JSX.Element => {
  const styles = usePortfoliosPracticesAccessStyles();
  const footerStyles = useSharedDialogFooterStyles();
  const contentStyles = useSharedDialogContentStyles();

  const [selectedUser, setSelectedUser] = useState<CustomOption[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      setSelectedUser(
        editingItem
          ? [
              toUserOption(
                editingItem.user.userId,
                editingItem.user.displayName,
              ),
            ]
          : [],
      );
      setFieldErrors({});
    }
  }, [isOpen, editingItem]);

  const handleSelectedUserChange = useCallback(
    (options: CustomOption[]): void => {
      const lastSelected = options.length ? [options[options.length - 1]] : [];
      setSelectedUser(lastSelected);
      setFieldErrors((prev) => ({ ...prev, user: "" }));
    },
    [],
  );

  const handleSave = async (): Promise<void> => {
    if (selectedUser.length === 0) {
      setFieldErrors({ user: "A user is required" });
      return;
    }

    try {
      await onSave({
        user: {
          userId: selectedUser[0].id ?? selectedUser[0].key,
          displayName: selectedUser[0].text,
        },
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to save";
      setFieldErrors((prev) => ({ ...prev, general: errorMessage }));
    }
  };

  const isSaveDisabled = isSaving || selectedUser.length === 0;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(_, d) => !d.open && !isSaving && onDismiss()}
    >
      <DialogSurface className={contentStyles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label="Close"
                onClick={onDismiss}
                disabled={isSaving}
              />
            }
          >
            {editingItem ? "Edit executive access" : "Add executive access"}
          </DialogTitle>
          <DialogContent>
            <div className={contentStyles.formContainer}>
              {isSaving && (
                <div className={contentStyles.formLoadingOverlay}>
                  <Spinner label="Saving executive access..." />
                </div>
              )}
              {fieldErrors.general && (
                <div role="alert" className={styles.errorAlert}>
                  {fieldErrors.general}
                </div>
              )}
              <Field
                label="User"
                required
                validationState={fieldErrors.user ? "error" : "none"}
                validationMessage={fieldErrors.user}
                className={styles.dialogField}
              >
                <PeoplePicker
                  selectedOptions={selectedUser}
                  handleSelectedItems={handleSelectedUserChange}
                  hasOnlyOurUsers
                  hasSingleSelection
                  disabled={isSaving}
                />
              </Field>
            </div>
          </DialogContent>
        </DialogBody>
        <div className={footerStyles.dialogFooter}>
          <DialogFooterAction
            icon={<CheckmarkRegular aria-hidden="true" />}
            onClick={() => void handleSave()}
            disabled={isSaveDisabled}
            tooltip="Save"
          />
        </div>
      </DialogSurface>
    </Dialog>
  );
};
